import {
  CODE_FONT_KEY,
  DEFAULT_FONT_KEY,
  type PdfFontKey,
} from "../fonts";
import type { ScriptFallbackKey } from "../script-fallbacks";
import {
  defaultPdfCodeTheme,
  type PdfCodeTheme,
} from "./code-themes";
import {
  defaultPdfDiagramTheme,
  defaultPdfTheme,
  type PdfDiagramTheme,
  type PdfTheme,
} from "./themes";

export type PaperSize = "a4" | "letter";
export type Orientation = "portrait" | "landscape";
export type MarginPreset = "narrow" | "normal" | "wide";
export type { PdfTheme, PdfDiagramTheme } from "./themes";
export type { PdfCodeTheme } from "./code-themes";

export type PdfOptions = {
  paperSize: PaperSize;
  orientation: Orientation;
  margins: MarginPreset;
  theme: PdfTheme;
  codeTheme: PdfCodeTheme;
  diagramTheme: PdfDiagramTheme;
  pageNumbers: boolean;
  fontFamily: PdfFontKey;
  fontSize: number;
  diagramFontFamily: PdfFontKey;
  diagramFontSize: number;
  codeFontFamily: PdfFontKey;
  codeFontSize: number;
};

/** preview = live iframe chrome; pdf = Puppeteer export (shared Paged.js). */
export type PrintDocumentVariant = "preview" | "pdf";

export type PrintDocumentInput = {
  title: string;
  bodyHtml: string;
  options: PdfOptions;
  /** Defaults to "pdf" so accidental callers do not get preview-only chrome. */
  variant?: PrintDocumentVariant;
  embeddedFontCss?: string;
  externalFontHref?: string;
  /** Detected Noto script fallbacks for this document (stack + Mermaid). */
  scriptFallbacks?: readonly ScriptFallbackKey[];
  /** Document writing direction (Arabic-majority markdown → rtl). */
  dir?: "ltr" | "rtl";
  /** Document language; "ar" when dir is rtl, otherwise "en". */
  lang?: string;
  /** Host preview-pane background so sheet gaps match the surface exactly. */
  previewSurfaceBackground?: string;
};

export const defaultPdfOptions: PdfOptions = {
  paperSize: "a4",
  orientation: "portrait",
  margins: "normal",
  theme: defaultPdfTheme,
  codeTheme: defaultPdfCodeTheme,
  diagramTheme: defaultPdfDiagramTheme,
  pageNumbers: false,
  fontFamily: DEFAULT_FONT_KEY,
  fontSize: 12,
  diagramFontFamily: DEFAULT_FONT_KEY,
  diagramFontSize: 12,
  codeFontFamily: CODE_FONT_KEY,
  codeFontSize: 10,
};

export const defaultPreviewSurfaceBackground = "#eef2f6";

export {
  mermaidScriptHref,
  pagedjsScriptHref,
} from "../vendor-assets.generated";

export const pagedPreviewCompleteEvent = "markdown2pdf:paged-complete";
export const pagedPreviewFallbackEvent = "markdown2pdf:paged-fallback";

/** Parent → warm preview iframe: repaginate without reloading the shell. */
export const pagedPreviewUpdateMessageType = "markdown2pdf:paged-update";

export type PagedPreviewUpdateMessage = {
  type: typeof pagedPreviewUpdateMessageType;
  revision: string;
  bodyHtml: string;
  printCss: string;
  dir: "ltr" | "rtl";
  lang: string;
  previewSurfaceBackground: string;
  mermaidConfig: Record<string, unknown>;
};
